// Access Logs and Login History-related filters

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const accessLogsFilters: FilterRegistry = {
	loginHistorySearch: {
		...createKeywordFilter({
			label: 'Search Users',
			placeholder: 'Search by name or email...',
			objectTable: 'login_history',
			listKeys: ['login_history'],
		}),
		queryParamName: 'loginHistorySearch',
		queryMapping: {
			objectTable: 'login_history',
			fieldName: 'user_search',
			joinType: 'direct',
		},
	},

	loginHistoryDateRange: {
		...createDateRangeFilter({
			label: 'Date Range',
			objectTable: 'login_history',
			dateField: 'date',
			listKeys: ['login_history'],
		}),
		queryParamName: 'loginHistoryDateRange',
	},

	loginHistoryStatus: {
		...createStatusFilter({
			label: 'Status',
			options: [
				{ id: 'success', label: 'Success' },
				{ id: 'failed', label: 'Failed' },
				{ id: 'locked', label: 'Locked' },
				{ id: 'expired', label: 'Expired' },
			],
			objectTable: 'login_history',
			fieldName: 'status',
			listKeys: ['login_history'],
			colorScheme: 'orange',
		}),
		queryParamName: 'loginHistoryStatus',
		allowClear: true,
	},

	loginHistoryAuthMethod: {
		...createStatusFilter({
			label: 'Auth Method',
			options: [
				{ id: 'password', label: 'Password' },
				{ id: 'microsoft', label: 'Microsoft' },
				{ id: 'oauth', label: 'OAuth' },
				{ id: 'token', label: 'Token' },
			],
			objectTable: 'login_history',
			fieldName: 'authMethod',
			listKeys: ['login_history'],
			colorScheme: 'green',
		}),
		queryParamName: 'loginHistoryAuthMethod',
		allowClear: true,
	},

	// PD-14200 — filter ecommerce logins by the client account(s) the portal
	// user is mapped to. Resolved tenant-side by the crm-api proxy into a set
	// of auth-server user ids (client_public_user → public_user.workdUserId);
	// only meaningful for ecommerce logins, hidden on the CRM Users list.
	loginHistoryClient: {
		type: 'multi-select',
		label: 'Client',
		placeholder: 'Type to search clients...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'loginHistoryClient',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		queryMapping: {
			objectTable: 'login_history',
			fieldName: 'clientIds',
			joinType: 'custom',
		},
		listKeys: ['login_history'],
	},

	// PD-14200 — filter ecommerce logins by the sales rep assigned (primary or
	// alternate) to the portal user's client(s). Same rep semantics as the
	// Ecommerce Users list. Locked to "Me" for users without
	// clients:detail:any (enforced server-side by the crm-api proxy).
	loginHistorySalesRep: {
		type: 'multi-select',
		label: 'Client Sales Rep',
		placeholder: 'Select sales rep...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'loginHistorySalesRep',
		multiple: true,
		options: [{ id: 'ME', label: 'Me' }],
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		queryMapping: {
			objectTable: 'login_history',
			fieldName: 'clientSalesRepIds',
			joinType: 'custom',
		},
		listKeys: ['login_history'],
	},

	loginHistoryUserType: {
		type: 'single-select',
		label: 'User Type',
		placeholder: 'Select user type...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'crm', label: 'CRM' },
			{ id: 'ecommerce', label: 'Ecommerce' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'loginHistoryUserType',
		multiple: false,
		queryMapping: {
			objectTable: 'login_history',
			fieldName: 'userType',
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['login_history'],
	},

	accessLogsUserType: {
		type: 'single-select',
		label: 'User Type',
		placeholder: 'Select user type...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'crm', label: 'CRM' },
			{ id: 'ecommerce', label: 'Ecommerce' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'accessLogsUserType',
		multiple: false,
		queryMapping: {
			objectTable: 'access_logs',
			fieldName: '_workd__user__id',
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['access_logs'],
	},

	apiAccessLogsUser: {
		...createEntityLookupFilter({
			label: 'User',
			placeholder: 'Select users...',
			apiEndpoint: '/users',
			objectTable: 'access_logs',
			fieldName: '_workd__user__id',
			listKeys: ['access_logs'],
			displayFields: {
				id: 'workdUserId',
				primary: 'display_name',
				secondary: 'email',
			},
			colorScheme: 'purple',
		}),
		queryParamName: 'apiAccessLogsUser',
		allowClear: true,
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	apiAccessLogsEndpoint: {
		type: 'multi-select',
		label: 'Activity',
		placeholder: 'Filter by activity type...',
		colorScheme: 'gray',
		searchable: true,
		loadOnFocus: true,
		allowClear: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/auth/access-logs/activity-types',
		queryParamName: 'apiAccessLogsEndpoint',
		queryMapping: {
			objectTable: 'access_logs',
			fieldName: 'endpointUrl',
			joinType: 'custom',
		},
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['access_logs'],
	},

	accessLogsDateRange: {
		...createDateRangeFilter({
			label: 'Date Range',
			objectTable: 'access_logs',
			dateField: 'date',
			listKeys: ['access_logs'],
		}),
		queryParamName: 'accessLogsDateRange',
	},
};
